import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Inbox, RefreshCw, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const STATUS = {
  confirmed: ['Booked', 'bg-sage-50 text-sage-800 border-sage-200'],
  needs_assignment: ['Assign room', 'bg-amber-50 text-amber-700 border-amber-200'],
  received: ['Received', 'bg-stone-100 text-stone-600 border-stone-200'],
  waiting: ['Waitlist', 'bg-sky-50 text-sky-700 border-sky-200'],
  cancelled: ['Cancelled', 'bg-red-50 text-red-600 border-red-200'],
};

const details = (r) => {
  if (r.type === 'room') return `${r.room_name} · ${r.check_in} → ${r.check_out} · ${r.units} unit(s)`;
  if (r.type === 'experience') return `${r.experience_title || 'Experience'} · ${r.experience_date || ''} ${r.experience_time || ''}`;
  return `${r.room_name || r.room_type || 'Any room'} · ${r.check_in || '?'} → ${r.check_out || '?'}`;
};

const CancelButton = ({ row, onDone }) => {
  if (row.status === 'cancelled') return null;
  const cancel = async () => {
    if (!window.confirm(`Cancel ${row.pms_reservation_id}? Linked bookings will be cancelled and the units released.`)) return;
    try { await api.post(`/site-reservations/${row.id}/cancel`); toast.success(`${row.pms_reservation_id} cancelled`); onDone(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Cancel failed'); }
  };
  return (
    <Button variant="ghost" size="sm" onClick={cancel} className="h-7 text-red-600 hover:bg-red-50" data-testid={`cancel-site-reservation-${row.pms_reservation_id}`}>
      <XCircle className="w-3.5 h-3.5 mr-1" />Cancel
    </Button>
  );
};

export const WebsiteRequestsTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get('/site-reservations'); setRows(res.data); }
    catch { toast.error('Failed to load website requests'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white border border-stone-200 rounded-md">
      <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
        <p className="font-semibold text-stone-800">Everything received from the GoNomikai website</p>
        <Button variant="ghost" size="sm" onClick={load} data-testid="refresh-site-reservations"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></Button>
      </div>
      {rows.length === 0 && !loading ? (
        <div className="p-12 text-center" data-testid="site-reservations-empty">
          <Inbox className="w-12 h-12 mx-auto mb-3 text-stone-300" />
          <p className="text-stone-500">No website requests yet.</p>
        </div>
      ) : (
        <Table>
          <TableHeader><TableRow>
            <TableHead>Reservation</TableHead><TableHead>Type</TableHead><TableHead>Guest</TableHead>
            <TableHead>Details</TableHead><TableHead>Guests</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead><TableHead />
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => {
              const [label, cls] = STATUS[r.status] || STATUS.received;
              return (
                <TableRow key={r.id} data-testid={`site-reservation-row-${r.pms_reservation_id}`}>
                  <TableCell className="font-mono text-sm text-sage-700">{r.pms_reservation_id}</TableCell>
                  <TableCell className="capitalize text-sm">{r.type}</TableCell>
                  <TableCell><p className="font-medium text-stone-800">{r.name || '—'}</p><p className="text-xs text-stone-400">{[r.email, r.phone].filter(Boolean).join(' · ')}</p></TableCell>
                  <TableCell className="text-sm">{details(r)}</TableCell>
                  <TableCell className="text-sm">{r.guests}</TableCell>
                  <TableCell className="text-right font-semibold">{r.total_amount != null ? `₹${Number(r.total_amount).toLocaleString('en-IN')}` : '—'}</TableCell>
                  <TableCell><span className={`text-xs px-2 py-0.5 rounded border ${cls}`}>{label}</span></TableCell>
                  <TableCell><CancelButton row={r} onDone={load} /></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
};
